import React, { useCallback } from 'react'
import { BASE_URL } from '../../../utils/constants/url'

const BACKEND_ORIGIN = (BASE_URL || '').replace(/\/api$/, '')

const resolveUrl = (url) => {
  if (!url) return url
  if (url.startsWith('http://') || url.startsWith('https://')) return url
  if (url.startsWith('/')) return `${BACKEND_ORIGIN}${url}`
  return url
}

// Box-model alignment → flex, mirroring the backend BuildElement box branch so
// the in-canvas edit render matches the server-rendered preview.
import { isPill } from './placementModel'
import { SPONSORS_FIELD_ID, SPONSOR_PREVIEW_COUNT, sponsorSlotsPct } from './overlayEditorControls'

const JUSTIFY = { left: 'flex-start', right: 'flex-end', center: 'center' }
const ALIGN = { top: 'flex-start', bottom: 'flex-end', middle: 'center' }

// Renders a placement as its box-model rect ({x,y,w,h} for text,
// {x,y,imageWidth,imageHeight} for image) with drag/resize handles. Text fields
// show their resolved text (preview text or sample) in the actual font family,
// weight, color and H/V alignment, with the font size scaled from the 1920-wide
// design space to the canvas via `scale`. This lets the operator judge size/fit
// without a server render; Preview mode remains authoritative.
export default function PlacementBadge({
  placement,
  field,
  isEditing,
  onClick,
  onMouseDown,
  onResize,
  text,
  scale = 0,
  aspectLocked = false,
}) {
  const handleMouseDown = (e) => {
    if (!onMouseDown) return
    e.stopPropagation()
    onMouseDown(e)
  }

  const isImage = field?.dataType === 'Image'
  const isBar = field?.dataType === 'Bar'
  const isColorBox = field?.dataType === 'ColorBox'
  const { x, y, fieldId } = placement
  const label = field?.label ?? fieldId

  const pill = isPill(placement)
  const w = pill ? (placement.bgWidth ?? 8)
    : isImage ? (placement.imageWidth ?? 8) : (placement.w ?? 20)
  const h = pill ? (placement.bgHeight ?? 2)
    : isImage ? (placement.imageHeight ?? 8) : (placement.h ?? 4)
  // Mirrors the renderer: a pill/bar anchors the BOX on x,y, shifted by its own edge
  // control (text anchor for a pill, fill direction for a bar) — y is always the centre.
  // A color box has no edge control: x,y are its top-left, so it takes no shift at all.
  const anchorEdge = pill ? placement.textAnchor : isBar ? (placement.hAlign ?? 'left') : null
  const anchorShift = anchorEdge
    ? `translate(${anchorEdge === 'left' ? '0' : anchorEdge === 'right' ? '-100%' : '-50%'}, -50%)`
    : undefined

  const hAlign = placement.hAlign ?? 'center'
  const vAlign = placement.vAlign ?? 'middle'
  const fit = placement.fit ?? 'wrap'

  // Resize handle: drags the bottom-right corner. Delta is converted from
  // mouse-px to canvas-percent using the placement box element's own
  // bounding rect (which we grab off the event's currentTarget's parent).
  const handleResizeMouseDown = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!onResize) return
    const boxEl = e.currentTarget.parentElement
    const canvasEl = boxEl?.offsetParent
    if (!canvasEl) return
    const canvasRect = canvasEl.getBoundingClientRect()
    const startX = e.clientX
    const startY = e.clientY
    const startW = w
    const startH = h
    const handleMove = (ev) => {
      const dxPct = ((ev.clientX - startX) / canvasRect.width) * 100
      const dyPct = ((ev.clientY - startY) / canvasRect.height) * 100
      const nextW = Math.max(1, +(startW + dxPct).toFixed(2))
      let nextH = Math.max(1, +(startH + dyPct).toFixed(2))
      if (isImage && aspectLocked) {
        // Keep the box aspect: driven by width, holding H%/W% constant (which
        // preserves the pixel aspect since both axes share the 1920×1080 canvas).
        nextH = Math.max(1, +(nextW * (startW > 0 ? startH / startW : 1)).toFixed(2))
      }
      onResize(fieldId, isImage ? { imageWidth: nextW, imageHeight: nextH } : { w: nextW, h: nextH })
    }
    const handleUp = () => {
      document.removeEventListener('mousemove', handleMove)
      document.removeEventListener('mouseup', handleUp)
    }
    document.addEventListener('mousemove', handleMove)
    document.addEventListener('mouseup', handleUp)
  }, [onResize, fieldId, isImage, w, h, aspectLocked])

  const imgSrc = isImage && field?.sampleValue ? resolveUrl(field.sampleValue) : null
  // The sponsor area is one drawn rectangle holding N logos at their 4:3 crop, derived
  // from the area and the arrangement exactly as the renderer derives them — so the
  // canvas shows the slots the operator is actually designing, not one stretched logo.
  const sponsorSlots = imgSrc && fieldId === SPONSORS_FIELD_ID
    ? sponsorSlotsPct(placement, SPONSOR_PREVIEW_COUNT)
    : null
  // Render faithful text only once the canvas has been measured (scale > 0) and
  // there's something to show; otherwise fall back to the {{field}} label.
  const showText = !isImage && !isBar && !isColorBox && scale > 0 && !!text

  return (
    <div
      onMouseDown={handleMouseDown}
      onClick={(e) => { e.stopPropagation(); onClick(e) }}
      style={{
        position: 'absolute',
        left: `${x}%`,
        top: `${y}%`,
        width: `${w}%`,
        height: `${h}%`,
        transform: anchorShift,
        boxSizing: 'border-box',
        border: isEditing ? '2px solid #1976d2' : '1px dashed rgba(25, 118, 210, 0.55)',
        background: pill ? (placement.bgColor ?? 'rgba(25, 118, 210, 0.35)')
          : imgSrc || showText ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
        cursor: 'pointer',
        userSelect: 'none',
        zIndex: 10,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          justifyContent: isImage ? 'center' : JUSTIFY[hAlign],
          alignItems: isImage ? 'center' : ALIGN[vAlign],
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
      >
        {isColorBox ? (
          <div style={{
            position: 'absolute', inset: 0,
            background: field?.sampleValue ?? '#7D66FC',
          }} />
        ) : isBar ? (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex',
            justifyContent: anchorEdge === 'right' ? 'flex-end' : anchorEdge === 'center' ? 'center' : 'flex-start',
          }}>
            <div style={{
              width: `${Math.min(100, Number(field?.sampleValue ?? 60))}%`,
              height: '100%',
              background: 'currentColor',
            }} />
          </div>
        ) : sponsorSlots ? (
          sponsorSlots.map((slot, i) => (
            <img
              key={i}
              src={imgSrc}
              alt=''
              draggable={false}
              style={{
                position: 'absolute',
                left: `${slot.left}%`,
                top: `${slot.top}%`,
                width: `${slot.width}%`,
                height: `${slot.height}%`,
                objectFit: 'contain',
              }}
            />
          ))
        ) : imgSrc ? (
          <img
            src={imgSrc}
            alt=''
            draggable={false}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : showText ? (
          <span
            style={{
              fontSize: `${(placement.fontSize ?? 32) * scale}px`,
              fontFamily: placement.fontFamily ? `'${placement.fontFamily}'` : "'Roboto', sans-serif",
              fontWeight: placement.fontWeight ?? 700,
              color: placement.color ?? '#ffffff',
              textAlign: hAlign,
              lineHeight: 1.2,
              maxWidth: '100%',
              // Mirrors the renderer: a line cap clamps and ellipsises, no cap wraps freely.
              ...(fit === 'trim'
                ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
                : placement.maxLines > 0
                  ? {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: placement.maxLines,
                    WebkitBoxOrient: 'vertical',
                  }
                  : { whiteSpace: 'normal', wordBreak: 'break-word' }),
            }}
          >
            {text}
          </span>
        ) : (
          <span style={labelStyle}>{`{{${label}}}`}</span>
        )}
      </div>
      {isEditing && (
        <div
          onMouseDown={handleResizeMouseDown}
          style={{
            position: 'absolute',
            right: -4,
            bottom: -4,
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: '#fff',
            border: '2px solid #1976d2',
            cursor: 'nwse-resize',
            zIndex: 11,
          }}
        />
      )}
    </div>
  )
}

// Small inline label — avoids pulling in @material-ui/core's Typography just
// for this one text node with fixed styling.
const labelStyle = {
  color: '#1976d2',
  fontSize: 11,
  fontWeight: 500,
  textAlign: 'center',
  padding: '0 4px',
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
}
